import { Box, Stack, Typography } from "@mui/material";
import { AddRelatedParty } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/Add/AddRelatedParty";
import { RelatedPartyFormWrapper } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/RelatedPartyFormWrapper";
import { type RelatedPartyDto, RelationType } from "Infrastructure/Api/Api";
import { BUSINESS_ROLE_RELATION_TYPES } from "Models/BusinessRelationTypes";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.RelatedParties;

type GroupedRelation = {
  relationType: RelationType;
  relationPublicID?: string | null;
};

type Props = {
  relatedParties: Array<RelatedPartyDto>;
  isReadOnly?: boolean;
  owningPartyID?: string | null;
};

export const RelatedPartiesAccordionList = ({
  relatedParties,
  isReadOnly,
  owningPartyID,
}: Props) => {
  const { t } = useResource();
  const filteredRelatedParties = relatedParties.filter(({ relationType }) =>
    [...BUSINESS_ROLE_RELATION_TYPES, RelationType.BeneficialOwner].includes(
      relationType,
    ),
  );

  const groupedParties = groupRelatedParties(filteredRelatedParties);

  return (
    <>
      {groupedParties.length === 0 ? (
        <Typography>{t(COMPONENT_RESOURCES.NoRelatedParties)}</Typography>
      ) : (
        <Stack>
          {groupedParties.map(({ relations, relatedPartyPublicID }) => {
            const primaryRelation =
              relations.find(
                ({ relationType }) =>
                  relationType !== RelationType.BeneficialOwner,
              ) ??
              relations.find(
                ({ relationType }) =>
                  relationType === RelationType.BeneficialOwner,
              );

            return (
              <RelatedPartyFormWrapper
                key={relatedPartyPublicID}
                relationType={
                  primaryRelation?.relationType ?? RelationType.BeneficialOwner
                }
                relationPublicID={primaryRelation?.relationPublicID}
                relatedPartyPublicID={relatedPartyPublicID}
                owningPartyID={owningPartyID}
                isOwner={relations.some(
                  ({ relationType }) =>
                    relationType === RelationType.BeneficialOwner,
                )}
                isReadOnly={isReadOnly}
              />
            );
          })}
        </Stack>
      )}
      {!isReadOnly && owningPartyID && (
        <Box mt={2}>
          <AddRelatedParty owningPartyID={owningPartyID} />
        </Box>
      )}
    </>
  );
};

/**
 * Groups parties with the same relatedPartyPublicID and collects all their relations (type + relation PublicID).
 * This is needed when a party has more roles (e.g. BeneficialOwner is also a StatutoryBody), otherwise they would appear in the form multiple times.
 */
const groupRelatedParties = (relatedParties: Array<RelatedPartyDto>) => {
  const groupedPartiesMap = new Map<string, Array<GroupedRelation>>();

  for (const {
    relatedPartyPublicID,
    relationType,
    publicID,
  } of relatedParties) {
    if (!groupedPartiesMap.has(relatedPartyPublicID)) {
      groupedPartiesMap.set(relatedPartyPublicID, []);
    }
    groupedPartiesMap
      .get(relatedPartyPublicID)
      ?.push({ relationType, relationPublicID: publicID });
  }

  return Array.from(groupedPartiesMap, ([relatedPartyPublicID, relations]) => ({
    relatedPartyPublicID,
    relations,
  }));
};
